import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { useConnection } from '@/entities/connection';
import { openCopilot } from '@/features/copilot';
import { DatasetScorePanel, useDatasetScore } from '@/features/scoring';
import { DatasetRulesPanel } from '@/features/rules';
import { useRequestProfile } from '@/features/profiler';

import { useDatasetFacts } from '../api/catalogue';
import { profileReading } from '../model/catalogue';
import { DatasetFigures } from './DatasetFigures';

import {
  diffVersions,
  qualifiedName,
  tracksRenames,
  verdictStillApplies,
} from '@/entities/dataset';
import type { Classification, DatasetVersion } from '@/shared/api/generated/catalog';
import { ProfileHistoryPanel, ProfilePanel } from '@/features/profiler';
import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { Button } from '@/shared/ui/Button';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { SCREEN_PATHS } from '@/shared/routes';
import { Tabs } from '@/shared/ui/Tabs';

import {
  NOT_CLASSIFIED,
  useClassification,
  useDataset,
  useRequestClassification,
  useSchemaHistory,
} from '../api/catalog';

/* The trail back up, as the design writes it: "Datasets / {connection}". **Datasets returns to
 * this connection's table** rather than to the source picker, because the sibling tables of the same
 * database are what a reader most wants to step back to. The connection is the page's own context
 * rather than a destination, so it is plain text.
 *
 * `source` may be absent — the connection read is in flight, failed, or past its cap — and then
 * Datasets leads to the picker and no name is shown: a UUID in a breadcrumb is the bare-id defect this
 * codebase has shipped four times. */
function Breadcrumbs({
  source,
}: {
  readonly source: { readonly id: string; readonly name: string } | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
      <Link
        to={
          source === null
            ? SCREEN_PATHS.datasets
            : `${SCREEN_PATHS.datasets}/source/${source.id}`
        }
      >
        {t('catalogue.title')}
      </Link>
      {source === null ? null : (
        <>
          <span aria-hidden="true" className="lq-breadcrumbs__sep">
            /
          </span>
          <span aria-current="page">{source.name}</span>
        </>
      )}
    </nav>
  );
}

/* The PII panel, as the design's bare card of rows: the column, what it was judged to hold, how it
 * was judged, how sure, and where the verdict stands. A deterministic rule and a model are told apart
 * in words because a reviewer trusts them differently; the confidence is a bar *and* a figure.
 *
 * The rule it enforces: only an APPROVED verdict with zero findings may read as "no PII found". Every
 * other empty verdict means nobody looked or the look failed, and those are not the same thing.
 * Classifying is the header's action, so it is not repeated here.
 */
function PiiPanel({
  classification,
  latest,
}: {
  readonly classification: Classification | typeof NOT_CLASSIFIED;
  readonly latest: DatasetVersion | undefined;
}): ReactElement {
  const { t } = useTranslation();

  if (classification === NOT_CLASSIFIED) {
    return <p className="lq-pii__none">{t('dataset.pii.never')}</p>;
  }

  const approvedAndClean =
    classification.state === 'approved' && classification.verdicts.length === 0;
  /* Computed here from the two stored checksums rather than read from a server flag, so the claim is
   * one the client can justify. */
  const stale =
    latest !== undefined &&
    !verdictStillApplies(latest, classification.classified_against);

  return (
    <>
      {classification.verdicts.length === 0 ? (
        <p className="lq-pii__none">
          {approvedAndClean ? t('review.pii.none') : t('review.pii.notAssessed')}
        </p>
      ) : (
        <ul className="lq-pii__rows" aria-label={t('dataset.pii.title')}>
          {classification.verdicts.map((verdict) => {
            const share = Math.round(Math.min(1, Math.max(0, verdict.confidence)) * 100);
            return (
              <li key={verdict.column_name} className="lq-pii__row">
                <span className="lq-pii__column">{verdict.column_name}</span>
                <span className="lq-pii__kind">{t(`pii.kind.${verdict.kind}`)}</span>
                <span className="lq-pii__method" data-method={verdict.method}>
                  {t(`dataset.pii.method.${verdict.method}`, {
                    defaultValue: verdict.method,
                  })}
                </span>
                <span className="lq-pii__conf">
                  <span className="lq-pii__track" aria-hidden="true">
                    <span
                      className="lq-pii__fill"
                      style={{ width: `${String(share)}%` }}
                    />
                  </span>
                  <span className="lq-pii__pct">
                    {t('dataset.pii.confidence', { share })}
                  </span>
                </span>
                <span className="lq-pii__state" data-state={classification.state}>
                  {t(`review.state.${classification.state}.label`)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* What the rows cannot say, beneath them: the schema moved since this was classified, why a
          classifier came back inconclusive, and who approved it before. */}
      {stale ? (
        <p className="lq-pii__foot lq-pii__foot--warn">
          {t('dataset.pii.checksumMoved')}
        </p>
      ) : null}
      {classification.detail != null && classification.detail !== '' ? (
        <p className="lq-pii__foot">
          {t('dataset.pii.reason', { reason: classification.detail })}
        </p>
      ) : null}
      {classification.approved_by !== null ? (
        <p className="lq-pii__foot">
          {t('review.previouslyApprovedBy', { who: classification.approved_by })}
        </p>
      ) : null}
    </>
  );
}

function SchemaHistory({
  versions,
}: {
  readonly versions: readonly DatasetVersion[];
}): ReactElement {
  const { t } = useTranslation();

  if (versions.length <= 1) {
    return (
      <p className="lq-muted" style={{ margin: 0 }}>
        {t('dataset.history.single')}
      </p>
    );
  }

  return (
    <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {versions.map((version, index) => {
        const previous = versions[index + 1];
        const changes = previous === undefined ? [] : diffVersions(version, previous);

        return (
          <li
            key={version.id}
            style={{
              borderLeft: '2px solid var(--border-subtle)',
              paddingLeft: 'var(--space-3)',
              paddingBottom: 'var(--space-3)',
            }}
          >
            <div
              style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline' }}
            >
              <strong className="lumiq-numeric">v{version.version_number}</strong>
              <span
                style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
              >
                {new Date(version.observed_at).toLocaleString()}
              </span>
            </div>
            {previous === undefined ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                {t('dataset.history.first')}
              </div>
            ) : (
              <ul
                style={{
                  margin: 'var(--space-1) 0 0',
                  paddingLeft: 'var(--space-4)',
                  fontSize: 'var(--text-sm)',
                }}
              >
                {changes.map((change) => (
                  <li key={`${change.kind}-${change.column}`}>
                    <span className="lumiq-numeric">{change.column}</span>{' '}
                    <span className="lq-muted">
                      {t(`dataset.change.${change.kind}`, {
                        before: change.before ?? '—',
                        after: change.after ?? '—',
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

const DATASET_TABS = [
  'profile',
  'history',
  'schema',
  'classification',
  'rules',
  'scores',
  'lineage',
] as const;

type DatasetTab = (typeof DATASET_TABS)[number];

export function DatasetDetail(): ReactElement {
  const { t } = useTranslation();
  const { datasetId = '' } = useParams<{ datasetId: string }>();
  /* The profile first, as the design orders it: what the data looks like is the question a reader
   * most often arrives with, and the schema is one tab away. */
  const [searchParams] = useSearchParams();
  const asked = searchParams.get('tab');
  /* A link may name the tab to open on — Scores' "Full decomposition" lands on the Score tab. Only a
     tab this page has is honoured; anything else opens on the profile. */
  const [tab, setTab] = useState<DatasetTab>(
    DATASET_TABS.find((each) => each === asked) ?? 'profile',
  );

  const dataset = useDataset(datasetId);
  const history = useSchemaHistory(datasetId);
  const classification = useClassification(datasetId);
  /* A 404 is the ordinary answer for a dataset no execution has covered, so this query's error state
   * is a fact rather than a failure — rendered as "not scored yet" beside the name rather than as an
   * alert. See `ScoreLine`. */
  const score = useDatasetScore(datasetId);
  /* The source this dataset was harvested from, for the middle breadcrumb. The same cached list read
   * every other screen makes, so it costs nothing after the catalogue has been visited — which, now
   * that a dataset is reached *through* its source, is how a reader almost always arrives here. */
  const source = useConnection(dataset.data?.connection_id ?? '');
  /* The three reads behind the summary line, each independently absent. `useDatasetFacts` takes a
   * list because it is built for a listing's page; one id is the degenerate case and shares the
   * listing's cache entry when a reader arrives from `SourceDatasets`. */
  const facts = useDatasetFacts(dataset.data === undefined ? [] : [dataset.data.id]);
  /* The two actions. Both mint one idempotency key per dataset and clear it on success, so the
   * `disabled` on each button is what stops a double-click starting the work twice — see the header
   * for why that matters more for the profile than for the classification. */
  const classify = useRequestClassification(datasetId);
  const profile = useRequestProfile(datasetId);

  const reading =
    dataset.data === undefined
      ? profileReading(undefined, { unread: facts.isError })
      : profileReading(facts.data?.get(dataset.data.id), { unread: facts.isError });

  if (dataset.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('dataset.loading')}
        </p>
        <SkeletonRows rows={6} columns={4} />
      </>
    );
  }

  if (dataset.isError) {
    return (
      <>
        <Alert tone="error" title={t('dataset.unreachable')}>
          {dataset.error.message}
        </Alert>
        <p className="lq-dd__back">
          <Link to="/datasets">{t('dataset.backToCatalog')}</Link>
        </p>
      </>
    );
  }

  const versions = history.data?.items ?? [];
  const latest = versions[0];
  const versionNumbers = new Map(versions.map((each) => [each.id, each.version_number]));
  const title = qualifiedName(dataset.data);

  return (
    <section aria-labelledby="dataset-heading" className="lq-dd">
      <Breadcrumbs
        source={
          source.data === undefined
            ? null
            : { id: source.data.id, name: source.data.name }
        }
      />

      {/* The design's header: the qualified name in mono, the facts that identify this version as
          chips beneath it, and the page's three actions on the right. */}
      <div className="lq-dd__head">
        <div className="lq-dd__identity">
          <div className="lq-otype-title">
            <DatasetObjectTypeIcon objectType={dataset.data.object_type} />
            <h1 id="dataset-heading" className="lq-dd__title">
              {title}
            </h1>
          </div>
          <div className="lq-dd__chips">
            <span
              className="lq-dd__chip"
              title={t(`catalog.state.${dataset.data.state}.meaning`)}
            >
              {t(`catalog.state.${dataset.data.state}.label`)}
            </span>
            <span className="lq-dd__chip">
              {t('dataset.chip.version', { version: dataset.data.current_version })}
            </span>
            {latest === undefined ? null : (
              <span className="lq-dd__chip">
                {t('dataset.chip.columns', { count: latest.columns.length })}
              </span>
            )}
            {source.data === undefined ? null : (
              <span className="lq-dd__chip">{source.data.name}</span>
            )}
          </div>
          {!tracksRenames(dataset.data) && (
            <p className="lq-dd__note">{t('catalog.noRenameAnchorDetail')}</p>
          )}
        </div>
        <div className="lq-dd__actions">
          {/* Opens the shell's drawer with a question about this table in the composer, unsent:
              asking costs a completion, opening does not. */}
          <button
            type="button"
            className="lq-btn lq-btn--copilot"
            onClick={() => {
              openCopilot({ question: t('dataset.askQuestion', { dataset: title }) });
            }}
          >
            {t('dataset.action.ask')}
          </button>
          {/* `Classify PII` before `Run profile`, cheapest first: a classification reads
              `information_schema` and a model, where a profile is one full scan per column of a
              customer's production table. Both are disabled while pending, which is what stops a
              double-click starting the work twice. */}
          <Button
            tone="secondary"
            busy={classify.isPending}
            disabled={classify.isPending}
            onClick={() => {
              classify.mutate();
            }}
          >
            {t('dataset.action.classify')}
          </Button>
          <Button
            tone="primary"
            busy={profile.isPending}
            disabled={profile.isPending}
            title={t('dataset.action.profileCost')}
            onClick={() => {
              profile.mutate();
            }}
          >
            {t('dataset.action.profile')}
          </Button>
        </div>
      </div>

      {/* What the two header actions did. Both were silent on failure before — a refused profile
          request left the button re-enabled and nothing else, which reads as "it worked". */}
      {classify.isError ? (
        <Alert tone="error" title={t('dataset.classifyFailed')}>
          {classify.error.message}
        </Alert>
      ) : null}
      {classify.isSuccess ? (
        <Alert tone="notice">{t('dataset.classifyDone')}</Alert>
      ) : null}
      {profile.isError ? (
        <Alert tone="error" title={t('dataset.profileFailed')}>
          {profile.error.message}
        </Alert>
      ) : null}
      {profile.isSuccess && tab !== 'profile' ? (
        <Alert tone="notice">{t('profile.runSuccess')}</Alert>
      ) : null}

      <DatasetFigures
        datasetId={datasetId}
        score={score}
        reading={reading}
        baseline={facts.data?.get(datasetId)?.profile_baseline}
      />

      <div className="lq-dd__tabs">
        <Tabs
          label={t('dataset.tabs.label')}
          active={tab}
          onSelect={setTab}
          tabs={[
            { id: 'profile', label: t('dataset.tab.profile') },
            { id: 'history', label: t('dataset.tab.history') },
            {
              id: 'schema',
              label: t('dataset.tab.schema'),
              count: latest?.columns.length,
            },
            { id: 'classification', label: t('dataset.tab.classification') },
            { id: 'rules', label: t('dataset.tab.rules') },
            { id: 'scores', label: t('dataset.tab.scores') },
            { id: 'lineage', label: t('dataset.tab.lineage') },
          ]}
        />
      </div>

      {tab === 'profile' && (
        /* The current version's column count is what "finished" means for a profile. Passing it
           lets the panel poll while a profile is running and stop the moment the stored count
           reaches it — a bound derived from the work rather than a timer somebody guessed. */
        <ProfilePanel
          datasetId={datasetId}
          expectedColumns={latest?.columns.length}
          versionNumbers={versionNumbers}
          onCompareRuns={() => {
            setTab('history');
          }}
        />
      )}

      {/* Beside the profile rather than beneath it: the profile tab is already a full reading of one
          run, and this one compares two of them. */}
      {tab === 'history' && (
        <ProfileHistoryPanel
          datasetId={datasetId}
          onGoToProfile={() => {
            setTab('profile');
          }}
        />
      )}

      {tab === 'schema' && (
        <div className="lq-stack">
          <article className="lq-dd__panel">
            {latest === undefined || latest.columns.length === 0 ? (
              <p className="lq-dd__schemaNone">{t('dataset.schemaNone')}</p>
            ) : (
              <DataTable
                caption={t('dataset.columnsCaption')}
                columns={[
                  { key: 'ordinal', label: t('dataset.column.ordinal'), numeric: true },
                  { key: 'name', label: t('dataset.column.name') },
                  { key: 'type', label: t('dataset.column.type') },
                  { key: 'nullable', label: t('dataset.column.nullable') },
                  { key: 'comment', label: t('dataset.column.comment') },
                ]}
              >
                {latest.columns.map((column, index) => (
                  <tr key={column.name}>
                    <td className="lq-table__num lq-muted">{index + 1}</td>
                    <td>
                      <span className="lq-mono">{column.name}</span>
                    </td>
                    <td className="lq-muted lq-mono">{column.data_type}</td>
                    <td className={column.nullable ? 'lq-muted' : undefined}>
                      {column.nullable ? t('dataset.nullable') : t('dataset.notNullable')}
                    </td>
                    <td className="lq-muted">{column.comment ?? ''}</td>
                  </tr>
                ))}
              </DataTable>
            )}
          </article>

          <Card title={t('dataset.history.title')} help={t('help.term.schemaHistory')}>
            {history.isPending ? (
              <p role="status" className="lq-muted">
                {t('dataset.history.loading')}
              </p>
            ) : history.isError ? (
              <Alert tone="error">{history.error.message}</Alert>
            ) : (
              <SchemaHistory versions={versions} />
            )}
          </Card>
        </div>
      )}

      {tab === 'classification' && (
        <article className="lq-pii">
          {classification.isPending ? (
            <p role="status" className="lq-pii__none">
              {t('dataset.pii.loading')}
            </p>
          ) : classification.isError ? (
            <Alert tone="error">{classification.error.message}</Alert>
          ) : (
            <PiiPanel classification={classification.data} latest={latest} />
          )}
        </article>
      )}

      {tab === 'rules' && (
        <DatasetRulesPanel
          datasetId={datasetId}
          qualified={title}
          connectionId={dataset.data.connection_id}
        />
      )}

      {/* The score, owned by `features/scoring`; the decomposition — which rule cost what — is the
          half a reader acts on. The profile baseline sits below it and is never blended with it. */}
      {tab === 'scores' && <DatasetScorePanel datasetId={datasetId} />}

      {/* A tab that leads to a screen rather than embedding one: the graph needs the full width and
          its own pan and zoom. `datasetId` is the parameter `LineageScreen` reads. */}
      {tab === 'lineage' && (
        <div className="lq-dd__lineage">
          <div className="lq-dd__lineageText">
            <h2 className="lq-dd__lineageTitle">
              {t('dataset.lineage.title', { dataset: title })}
            </h2>
            <p className="lq-dd__lineageBody">{t('dataset.lineage.body')}</p>
          </div>
          <Link
            className="lq-btn lq-btn--primary"
            to={`${SCREEN_PATHS.lineage}?datasetId=${encodeURIComponent(datasetId)}`}
          >
            {t('dataset.lineage.open')}
          </Link>
        </div>
      )}
    </section>
  );
}
